import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X, FileText, CheckCircle2 } from "lucide-react";
import { API } from "../lib/api";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";
const MAX_FILES = 6;
const MAX_BYTES = 15 * 1024 * 1024;

export default function FileUploader({ files, setFiles, testId = "file-uploader" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    if (files.length + list.length > MAX_FILES) {
      toast.error(`You may attach up to ${MAX_FILES} files.`);
      return;
    }
    setBusy(true);
    for (const f of list) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} exceeds 15 MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await fetch(`${API}/uploads`, { method: "POST", body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Upload failed (${res.status})`);
        }
        const data = await res.json();
        setFiles((cur) => [...cur, data]);
      } catch (e) {
        toast.error(`${f.name}: ${e.message}`);
      }
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => setFiles((cur) => cur.filter((f) => f.id !== id));

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid={`${testId}-pick-btn`}
        className="w-full inline-flex items-center justify-center gap-2 h-12 border border-dashed border-[#C8A96A]/60 text-[#0B1F3A] text-[12px] tracking-[0.14em] uppercase hover:bg-[#C8A96A]/8 hover:border-[#C8A96A] transition-colors duration-200 disabled:opacity-60"
      >
        <Paperclip size={16} strokeWidth={1.25} />
        {busy ? "Uploading…" : files.length ? "Attach another report" : "Attach medical reports (PDF, JPG, PNG)"}
      </button>

      {files.length > 0 && (
        <ul className="mt-4 space-y-2">
          {files.map((f) => (
            <li key={f.id} data-testid={`${testId}-file-${f.id}`} className="flex items-center gap-3 bg-[#F5F7FA] px-3 py-2.5 border border-[#E6E9EF]">
              <FileText size={16} strokeWidth={1.25} className="text-[#C8A96A] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#0B1F3A] truncate">{f.filename}</div>
                <div className="text-[11px] text-[#0B1F3A]/50">{(f.size / 1024).toFixed(1)} KB</div>
              </div>
              <CheckCircle2 size={16} strokeWidth={1.25} className="text-[#0B1F3A]/60 shrink-0" />
              <button type="button" onClick={() => remove(f.id)} aria-label="Remove" className="text-[#0B1F3A]/40 hover:text-[#0B1F3A]">
                <X size={16} strokeWidth={1.25} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-[#0B1F3A]/45">
        Files are encrypted and stored privately. Only your assigned concierge and treating specialists will access them.
      </p>
    </div>
  );
}
